"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { ChevronDown, Menu, X } from "lucide-react";
import { getHeaderData } from "@/data/header";
import {
  ServiceSubmenu,
  ServiceMobileSubmenu,
} from "../sections/service-submenu";
import { AboutSubmenu, AboutMobileSubmenu } from "../sections/about-submenu";
import { CareerSubmenu, CareerMobileSubmenu } from "../sections/career-submenu";

interface HeaderProps {
  lang: "en" | "jp";
}

const Header = ({ lang }: HeaderProps) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isServicesDropdownOpen, setIsServicesDropdownOpen] = useState(false);
  const [isAboutDropdownOpen, setIsAboutDropdownOpen] = useState(false);
  const [isCareerDropdownOpen, setIsCareerDropdownOpen] = useState(false);
  const [isMobileServicesOpen, setIsMobileServicesOpen] = useState(false);
  const [isMobileAboutOpen, setIsMobileAboutOpen] = useState(false);
  const [isMobileCareerOpen, setIsMobileCareerOpen] = useState(false);
  const [dropdownTimeout, setDropdownTimeout] = useState<NodeJS.Timeout | null>(
    null
  );
  const pathname = usePathname();

  const navLinks = getHeaderData();

  const localeInfo = {
    en: {
      current: "EN",
      opposite: "JP",
      oppositeLocale: "jp",
    },
    jp: {
      current: "JP",
      opposite: "EN",
      oppositeLocale: "en",
    },
  };

  // Handle scroll for header styles
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // Close mobile menu on resize to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        // lg breakpoint
        setIsMobileMenuOpen(false);
        setIsMobileServicesOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Close mobile dropdowns when mobile menu is closed
  useEffect(() => {
    if (!isMobileMenuOpen) {
      setIsMobileServicesOpen(false);
      setIsMobileAboutOpen(false);
      setIsMobileCareerOpen(false);
    }
  }, [isMobileMenuOpen]);

  // Handle dropdown hover with delay
  const handleDropdownEnter = (
    dropdownType: "services" | "about" | "career"
  ) => {
    if (dropdownTimeout) {
      clearTimeout(dropdownTimeout);
      setDropdownTimeout(null);
    }
    if (dropdownType === "services") {
      setIsServicesDropdownOpen(true);
      setIsAboutDropdownOpen(false);
      setIsCareerDropdownOpen(false);
    } else if (dropdownType === "about") {
      setIsAboutDropdownOpen(true);
      setIsServicesDropdownOpen(false);
      setIsCareerDropdownOpen(false);
    } else if (dropdownType === "career") {
      setIsCareerDropdownOpen(true);
      setIsServicesDropdownOpen(false);
      setIsAboutDropdownOpen(false);
    }
  };

  const handleDropdownLeave = () => {
    const timeout = setTimeout(() => {
      setIsServicesDropdownOpen(false);
      setIsAboutDropdownOpen(false);
      setIsCareerDropdownOpen(false);
    }, 150); // 150ms delay
    setDropdownTimeout(timeout);
  };

  // Close dropdown immediately when a submenu link is clicked
  const handleSubmenuLinkClick = () => {
    if (dropdownTimeout) {
      clearTimeout(dropdownTimeout);
      setDropdownTimeout(null);
    }
    setIsServicesDropdownOpen(false);
    setIsAboutDropdownOpen(false);
    setIsCareerDropdownOpen(false);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (dropdownTimeout) {
        clearTimeout(dropdownTimeout);
      }
    };
  }, [dropdownTimeout]);

  // const switchLanguage = () => {
  //   const newLocale = localeInfo[lang as keyof typeof localeInfo]?.oppositeLocale || 'en';
  //   const pathWithoutLocale = pathname.replace(`/${lang}`, '') || '/';
  //   router.push(`/${newLocale}${pathWithoutLocale}`);
  // };

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 bg-white ${
        isScrolled ? "shadow-lg" : "backdrop-blur-sm"
      }`}
    >
      <div
        className={`container mx-auto transition-all duration-300 px-4 2xl:px-0 ${
          isScrolled ? "py-1" : "py-[22px]"
        }`}
      >
        <div className="flex justify-between items-center">
          {/* Logo */}
          <Link href={`/${lang}`} className="flex-shrink-0">
            <Image
              src="/images/logo.svg"
              alt="Nippon Koei Bangladesh"
              width={170}
              height={54}
              className="h-8 sm:h-10 lg:h-12 w-auto"
              priority
              draggable={false}
            />
          </Link>

          {/* Desktop Navigation and Language Switcher */}
          <nav className="hidden lg:flex items-center space-x-6 xl:space-x-8">
            {navLinks.map((link) => {
              const isActive =
                pathname === `/${lang}${link.path}` ||
                (link.path === "/" && pathname === `/${lang}`);

              if (link.hasDropdown) {
                const isDropdownOpen =
                  link.id === "services"
                    ? isServicesDropdownOpen
                    : link.id === "about"
                      ? isAboutDropdownOpen
                      : isCareerDropdownOpen;
                const dropdownType =
                  link.id === "services"
                    ? "services"
                    : link.id === "about"
                      ? "about"
                      : "career";

                return (
                  <div
                    key={link.id}
                    className="relative group flex items-center gap-1 h-12 cursor-pointer"
                    onMouseEnter={() => handleDropdownEnter(dropdownType)}
                    onMouseLeave={handleDropdownLeave}
                  >
                    <Link
                      href={`/${lang}${link.path}`}
                      className={`header-link text-gray-900 font-inter text-sm xl:text-base font-medium leading-4 capitalize transition-colors duration-200 hover:text-primary-800${
                        isActive || isDropdownOpen
                          ? " text-primary-800 active"
                          : ""
                      }`}
                    >
                      {link.name}
                      {/* underline handled by CSS */}
                    </Link>
                    <ChevronDown
                      size={14}
                      className={`transition-transform duration-200 ${
                        isDropdownOpen ? "rotate-180" : ""
                      }`}
                    />

                    {/* Dropdown */}
                    <div
                      className={`fixed top-[54px] left-0 w-screen transition-all duration-200 z-40 ${
                        isDropdownOpen
                          ? "opacity-100 visible translate-y-0"
                          : "opacity-0 invisible -translate-y-2"
                      }`}
                      style={{
                        top: `${isScrolled ? "52px" : "70px"}`, // Adjust based on header height
                      }}
                      // onMouseEnter={() => handleDropdownEnter(dropdownType)}
                      // onMouseLeave={handleDropdownLeave}
                    >
                      {link.id === "services" ? (
                        <ServiceSubmenu
                          open={isServicesDropdownOpen}
                          lang={lang}
                          onLinkClick={handleSubmenuLinkClick}
                        />
                      ) : link.id === "about" ? (
                        <AboutSubmenu
                          open={isAboutDropdownOpen}
                          lang={lang}
                          onLinkClick={handleSubmenuLinkClick}
                        />
                      ) : (
                        <CareerSubmenu
                          open={isCareerDropdownOpen}
                          lang={lang}
                          onLinkClick={handleSubmenuLinkClick}
                        />
                      )}
                    </div>
                  </div>
                );
              }

              return (
                <Link
                  key={link.id}
                  href={`/${lang}${link.path}`}
                  className={`header-link text-gray-900 font-inter text-sm xl:text-base font-medium leading-4 capitalize transition-colors duration-200 hover:text-primary-800${
                    isActive ? " text-primary-800 active" : ""
                  }`}
                >
                  {link.name}
                  {/* underline handled by CSS */}
                </Link>
              );
            })}

            {/* Language Switcher */}
            {/* <button
              disabled
              className="flex items-center space-x-2 px-3 py-2 text-xs xl:text-sm font-medium transition-colors hover:opacity-80"
            >
              <Globe size={12} className="xl:w-4 xl:h-4 text-gray-400" />
              <span className="font-semibold text-gray-700">{localeInfo[lang as keyof typeof localeInfo]?.current || 'EN'}</span>
            </button> */}
          </nav>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors"
            aria-label="Toggle mobile menu"
          >
            {isMobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        {/* Mobile Navigation Menu */}
        {isMobileMenuOpen && (
          <nav className="lg:hidden mt-4 pb-4 border-t border-gray-200 animate-fade-in max-h-dvh overflow-y-auto">
            <div className="flex flex-col space-y-3 pt-4">
              {navLinks.map((link) => {
                const isActive =
                  pathname === `/${lang}${link.path}` ||
                  (link.path === "/" && pathname === `/${lang}`);

                if (link.hasDropdown) {
                  const isMobileDropdownOpen =
                    link.id === "services"
                      ? isMobileServicesOpen
                      : link.id === "about"
                        ? isMobileAboutOpen
                        : isMobileCareerOpen;
                  const toggleMobileDropdown =
                    link.id === "services"
                      ? () => setIsMobileServicesOpen(!isMobileServicesOpen)
                      : link.id === "about"
                        ? () => setIsMobileAboutOpen(!isMobileAboutOpen)
                        : () => setIsMobileCareerOpen(!isMobileCareerOpen);

                  return (
                    <div key={link.id} className="flex flex-col">
                      {/* Main link with dropdown toggle */}
                      <div className="flex items-center justify-between">
                        <Link
                          href={`/${lang}${link.path}`}
                          onClick={() => setIsMobileMenuOpen(false)}
                          className={`font-medium text-base py-2 transition-colors duration-200 hover:text-primary-800 flex-1 ${
                            isActive ? "text-primary-800" : "text-gray-900"
                          }`}
                        >
                          {link.name}
                        </Link>
                        <button
                          onClick={toggleMobileDropdown}
                          className="p-2 text-gray-600 hover:text-gray-900 transition-colors"
                          aria-label={`Toggle ${link.id} submenu`}
                        >
                          <ChevronDown
                            size={16}
                            className={`transition-transform duration-200 ${
                              isMobileDropdownOpen ? "rotate-180" : ""
                            }`}
                          />
                        </button>
                      </div>

                      {/* Mobile Submenu */}
                      <div
                        className={`overflow-hidden transition-all duration-500 ease-in-out ${
                          isMobileDropdownOpen ? "max-h-[1000px]" : "max-h-0"
                        }`}
                        style={{
                          transitionTimingFunction:
                            "cubic-bezier(0.4, 0, 0.2, 1)",
                        }}
                      >
                        {link.id === "services" ? (
                          <ServiceMobileSubmenu
                            isOpen={isMobileDropdownOpen}
                            lang={lang}
                            onLinkClick={() => setIsMobileMenuOpen(false)}
                          />
                        ) : link.id === "about" ? (
                          <AboutMobileSubmenu
                            isOpen={isMobileDropdownOpen}
                            lang={lang}
                            onLinkClick={() => setIsMobileMenuOpen(false)}
                          />
                        ) : (
                          <CareerMobileSubmenu
                            isOpen={isMobileDropdownOpen}
                            lang={lang}
                            onLinkClick={() => setIsMobileMenuOpen(false)}
                          />
                        )}
                      </div>
                    </div>
                  );
                }

                return (
                  <Link
                    key={link.id}
                    href={`/${lang}${link.path}`}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`font-medium text-base py-2 transition-colors duration-200 hover:text-primary-800 ${
                      isActive ? "text-primary-800" : "text-gray-900"
                    }`}
                  >
                    {link.name}
                  </Link>
                );
              })}
            </div>

            {/* Mobile Language Switcher */}
            {/* <div className="pt-4 mt-4 border-t border-gray-200">
              <button
                disabled
                className="flex items-center space-x-2 px-4 py-3 rounded-md text-sm font-medium bg-gray-100 hover:bg-gray-200 transition-colors w-full border border-gray-300"
              >
                <Globe size={16} className="text-gray-400" />
                <span className="font-semibold">
                  {localeInfo[lang as keyof typeof localeInfo]?.current || "EN"}
                </span>
              </button>
            </div> */}
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;
